
import { Helmet } from "react-helmet";
import { MessageSquare, Brain, Code, Sparkles } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const WhatIsClaude = () => {
  return (
    <>
      <Helmet>
        <title>What is Claude AI? | World of AI</title>
        <meta name="description" content="Learn about Claude AI, Anthropic's advanced AI assistant and how it compares to other language models." />
      </Helmet>

      <main className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="mb-12 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900">What is Claude AI?</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive guide to Anthropic's advanced AI assistant focused on helpfulness, harmlessness, and honesty.
            </p>
          </header>

          <div className="mb-12">
            <img 
              src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485" 
              alt="AI Technology Concept" 
              className="w-full h-64 md:h-96 object-cover rounded-xl shadow-md mb-8"
            />
            
            <Alert className="mb-8 bg-purple-50 border-purple-200">
              <MessageSquare className="h-5 w-5 text-purple-600" />
              <AlertTitle className="text-purple-800">Safety-Focused AI</AlertTitle>
              <AlertDescription className="text-purple-700">
                Claude AI is designed with a focus on being helpful, harmless, and honest, with robust safeguards against harmful outputs.
              </AlertDescription>
            </Alert>

            <div className="prose prose-lg max-w-none text-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction to Claude AI</h2>
              <p>
                Claude is an AI assistant created by Anthropic, a safety-focused AI research company. Launched in 2022, Claude has quickly 
                become known for its conversational capabilities, nuanced understanding, and commitment to safety and ethical AI development.
              </p>
              <p>
                Unlike some AI systems focused primarily on capabilities, Claude was developed with a "Constitutional AI" approach that 
                emphasizes helpful, harmless, and honest interactions. This has positioned Claude as a preferred choice for businesses 
                and users who prioritize responsible AI use.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">How Claude AI Works</h2>
              <p>
                Claude is built on a large language model (LLM) architecture that has been trained on diverse text data from the internet and books.
                What makes Claude unique is Anthropic's approach to alignment - the process of ensuring AI systems act in accordance with human values and intentions.
              </p>
              <p>
                Anthropic uses a technique called Constitutional AI (CAI), which involves training Claude using a set of principles (a "constitution")
                that guide its responses. This helps Claude avoid harmful, unethical, or misleading outputs while still providing helpful information.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-white/70 backdrop-blur-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Brain className="h-6 w-6 text-purple-600" />
                </div>
                <CardTitle>Constitutional AI</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Claude is built with Constitutional AI principles, designed to be helpful while avoiding harmful, unethical, or misleading outputs.</p>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Code className="h-6 w-6 text-purple-600" />
                </div>
                <CardTitle>Long Context Window</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Claude can process up to 200,000 tokens (approximately 150,000 words) in a single conversation, making it ideal for analyzing long documents.</p>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Sparkles className="h-6 w-6 text-purple-600" />
                </div>
                <CardTitle>Multimodal Capabilities</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Claude can understand images along with text, allowing for richer interactions and the ability to analyze visual content alongside written information.</p>
              </CardContent>
            </Card>
          </div>

          <div className="mb-12 bg-white/80 rounded-xl p-8 shadow-md">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Claude Versions and Capabilities</h2>
            <p className="mb-6 text-gray-700">
              Anthropic has released several versions of Claude, each with improved capabilities:
            </p>

            <div className="space-y-4">
              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <h3 className="font-semibold text-gray-900">Claude 1</h3>
                <p className="text-gray-600">The initial release of Claude with strong conversational abilities and basic reasoning.</p>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <h3 className="font-semibold text-gray-900">Claude 2</h3>
                <p className="text-gray-600">Improved reasoning, more nuanced understanding, and better performance on complex tasks.</p>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <h3 className="font-semibold text-gray-900">Claude 3 Family (Haiku, Sonnet, Opus)</h3>
                <p className="text-gray-600">The latest generation with tiered models for different needs. Opus is the most capable, Sonnet balances performance and efficiency, and Haiku is optimized for speed.</p>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="bg-white/80 rounded-xl p-6 shadow-md">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-lg font-medium">How does Claude compare to ChatGPT?</AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  While both are powerful AI assistants, Claude is often noted for its nuanced understanding of complex prompts, longer context window, 
                  and strong focus on safety and alignment. ChatGPT may have advantages in certain specialized knowledge domains and has a larger ecosystem 
                  of plugins and integrations. The choice between them often depends on specific use cases and preferences regarding safety guardrails.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2">
                <AccordionTrigger className="text-lg font-medium">Is Claude free to use?</AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  Anthropic offers Claude with both free and paid tiers. Claude.ai provides free access with some usage limitations. 
                  Claude Pro ($20/month) offers higher usage limits and priority access. Enterprise solutions are also available 
                  for businesses requiring customization, higher security, and dedicated support.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-lg font-medium">What can Claude be used for?</AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  Claude excels at a wide range of tasks including drafting and editing content, answering questions, summarizing documents, 
                  creative writing, programming assistance, data analysis, learning complex topics, brainstorming ideas, and 
                  translating languages. Its long context window makes it particularly useful for analyzing lengthy documents.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger className="text-lg font-medium">What are Claude's limitations?</AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  Claude has several limitations: it may occasionally provide incorrect information (though typically with less frequency than some competitors), 
                  it has knowledge limitations for events after its training cutoff date, it cannot access the internet or run code in real-time, 
                  and its safety filters may sometimes be overly cautious, preventing it from addressing certain topics even in educational contexts.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <div className="bg-purple-50 rounded-xl p-8 shadow-md">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">The Future of Claude AI</h2>
            <p className="text-gray-700 mb-4">
              Anthropic continues to develop Claude with a focus on safety and alignment while improving capabilities. Future development likely includes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
              <li>Enhanced reasoning and problem-solving abilities</li>
              <li>Improved factuality and reduced hallucinations</li>
              <li>Better handling of specialized knowledge domains</li>
              <li>More advanced multimodal capabilities (text, image, potentially audio)</li>
              <li>Stronger tools for customization by organizations</li>
            </ul>
            <p className="text-gray-700">
              As AI assistants like Claude continue to evolve, they're likely to become increasingly valuable partners for knowledge work, 
              creative tasks, and complex information processing, while maintaining Anthropic's commitment to responsible development.
            </p>
          </div>
        </div>
      </main>
    </>
  );
};

export default WhatIsClaude;
